import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import CtaStrip from '../components/CtaStrip';
import Footer from '../components/Footer';

const caseStudies = [
  {
    id: 1,
    client: 'UK Logistics Company',
    industry: 'Logistics & Supply Chain',
    module: 'D365 CE Sales',
    service: 'Plugin Development + Power Automate',
    accent: 'var(--ms-blue)',
    bg: 'var(--ms-blue-pale)',
    icon: '🚛',
    challenge: 'The client was manually processing 200+ freight quotes per day inside D365. Sales reps had to copy data between three screens to generate a quote, attach a PDF, and email it to the customer — a process that took 15–20 minutes per quote and was prone to data entry errors.',
    solution: 'Built a server-side C# plugin that auto-calculated freight rates based on route, weight, and volume fields on the Quote entity. The plugin ran pre-operation, validating the margin threshold before save and blocking quotes below 12% gross margin. A Power Automate flow then generated a formatted PDF quote using a Word template and emailed it to the customer automatically on quote activation.',
    results: [
      'Quote generation time reduced from 15–20 min to under 2 min per quote',
      'Zero margin errors since go-live — plugin enforces minimum margin at save',
      'Estimated 6 hours of manual work saved per sales rep per week',
      'Client reported a 23% increase in quote volume within the first month',
    ],
    tech: ['C# IPlugin', 'Power Automate', 'Word Templates', 'D365 CE Sales', 'Quote Entity'],
    duration: '3 weeks',
  },
  {
    id: 2,
    client: 'Australian Financial Services Firm',
    industry: 'Financial Services',
    module: 'D365 Customer Service',
    service: 'PVA Chatbot + D365 Integration',
    accent: 'var(--ms-teal)',
    bg: '#DFF6F0',
    icon: '🏦',
    challenge: 'The client\'s customer service team was handling 400+ inbound queries per day via email and phone — primarily account balance checks, statement requests, and loan status queries. Each query required an agent to log into D365, look up the customer, and manually respond. Average handling time was 4–6 minutes per query.',
    solution: 'Designed and built a Power Virtual Agents chatbot deployed on the client\'s website and Microsoft Teams. The bot authenticated customers using their account number and postcode, then called Power Automate flows to query D365 Customer Service in real time — returning account balances, statement download links, and loan status. Complex queries were escalated to a live agent via D365 Omnichannel with full conversation context passed through.',
    results: [
      '67% of inbound queries resolved by the bot without agent involvement',
      'Average handling time for bot-resolved queries: under 90 seconds',
      'Agent workload reduced by approximately 270 queries per day',
      'Customer satisfaction score increased from 3.8 to 4.6 / 5 within 8 weeks',
    ],
    tech: ['Power Virtual Agents', 'Power Automate', 'D365 Customer Service', 'Omnichannel', 'Dataverse'],
    duration: '5 weeks',
  },
  {
    id: 3,
    client: 'Netherlands Manufacturing Group',
    industry: 'Manufacturing & Distribution',
    module: 'D365 Finance & Operations',
    service: 'Data Migration + Custom Integration',
    accent: 'var(--ms-purple)',
    bg: '#F4F0FF',
    icon: '🏭',
    challenge: 'The client was migrating from a legacy ERP system to D365 Finance & Operations. They had 11 years of transactional data — 340,000 customer records, 1.2M invoice lines, and 85,000 product SKUs — stored in an on-premise SQL Server database with no standardised data model. The existing data had duplicate customers, inconsistent address formats, and missing VAT numbers.',
    solution: 'Designed and executed a full data migration plan in three phases. Phase 1: data profiling and cleansing using SQL scripts to deduplicate customers, standardise addresses using a postcode API, and validate VAT numbers against the EU VIES service. Phase 2: transformation layer mapping legacy fields to D365 Finance data entities. Phase 3: staged migration using D365 Data Management Framework with full reconciliation reports after each batch.',
    results: [
      '340,000 customer records migrated with zero data loss',
      'Duplicate rate reduced from 18% to 0.3% post-migration',
      '99.97% VAT number validation accuracy across all records',
      'Migration completed 4 days ahead of the agreed go-live date',
    ],
    tech: ['D365 Finance & Operations', 'Data Management Framework', 'SQL Server', 'SSIS', 'EU VIES API'],
    duration: '8 weeks',
  },
  {
    id: 4,
    client: 'UK Professional Services Firm',
    industry: 'Professional Services',
    module: 'D365 CE + Power Platform',
    service: 'PCF Control + Web Resources',
    accent: 'var(--ms-orange)',
    bg: '#FFF1EC',
    icon: '⚖️',
    challenge: 'The client managed client matters inside D365 CE. Fee earners needed to log time against matters directly on the D365 form — but the standard D365 subgrid for time entries was slow, required too many clicks, and had no visual summary of hours billed vs budget. Fee earners were logging time in spreadsheets and updating D365 at the end of the week, causing billing delays.',
    solution: 'Built a custom PCF dataset control that replaced the standard time entry subgrid with an interactive weekly timesheet grid. Fee earners could enter time by clicking a cell in a matter × day grid, with running totals calculated in real time. A colour-coded budget consumption bar showed hours used vs budget at a glance. Data wrote back to the standard D365 time entry entity — no custom tables required.',
    results: [
      'Time entry compliance increased from 61% same-day to 94% same-day',
      'Average time to log a week\'s entries reduced from 12 min to under 3 min',
      'Billing cycle accelerated — invoices raised 3 days earlier on average',
      'Zero additional licences or infrastructure required',
    ],
    tech: ['PCF Dataset Control', 'TypeScript', 'React', 'D365 CE', 'Dataverse REST API'],
    duration: '4 weeks',
  },
];

export default function CaseStudiesPage({ onNavigate }) {
  const [expanded, setExpanded] = useState(null);

  return (
    <div>
      <Helmet>
        <title>D365 Case Studies — Real Projects, Real Results | AdjustD365</title>
        <meta name="description" content="Real Dynamics 365 project case studies — plugin development, PVA chatbots, data migration, and PCF controls. See the challenges, solutions, and measurable results delivered by AdjustD365." />
        <link rel="canonical" href="https://adjustd365.com/case-studies" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          'name': 'AdjustD365 Case Studies',
          'description': 'Dynamics 365 project case studies with measurable results',
          'itemListElement': caseStudies.map((c, i) => ({
            '@type': 'ListItem',
            'position': i + 1,
            'name': `${c.service} for ${c.client}`,
            'description': c.challenge.slice(0, 160),
          })),
        })}</script>
      </Helmet>

      {/* PAGE HERO */}
      <div className="section-pad" style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', padding: '56px 48px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-s)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', color: 'var(--text-s)', cursor: 'pointer', fontSize: '13px' }}>Home</button>
            <span>›</span>
            <span style={{ color: 'var(--ms-blue)', fontWeight: 500 }}>Case Studies</span>
          </div>
          <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '10px' }}>Proof of Work</div>
          <h1 style={{ fontSize: 'clamp(26px,3vw,42px)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-h)', marginBottom: '12px' }}>
            Real D365 Projects. Measurable Results.
          </h1>
          <p style={{ fontSize: '16px', color: 'var(--text-m)', maxWidth: '600px', lineHeight: 1.7 }}>
            Every case study below is a real project — real challenges, real solutions, and numbers that came from the client's own data after go-live.
          </p>
        </div>
      </div>

      {/* CASE STUDIES */}
      <section className="section-pad" style={{ padding: '80px 48px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {caseStudies.map((c) => {
            const isOpen = expanded === c.id;
            return (
              <article key={c.id} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', borderTop: `4px solid ${c.accent}` }}>

                {/* Header */}
                <div
                  style={{ padding: '32px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px' }}
                  onClick={() => setExpanded(isOpen ? null : c.id)}
                >
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flex: 1 }}>
                    <div style={{ width: '52px', height: '52px', borderRadius: '8px', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', flexShrink: 0 }}>
                      {c.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', color: c.accent, background: c.bg }}>{c.industry}</span>
                        <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', color: 'var(--text-s)', background: 'var(--bg-3)' }}>{c.module}</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-s)', padding: '2px 8px' }}>⏱ {c.duration}</span>
                      </div>
                      <h2 style={{ fontSize: 'clamp(16px,1.8vw,20px)', fontWeight: 700, color: 'var(--text-h)', marginBottom: '6px', letterSpacing: '-0.01em' }}>
                        {c.service}
                      </h2>
                      <div style={{ fontSize: '13px', color: 'var(--text-s)' }}>{c.client}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: '20px', color: 'var(--text-s)', flexShrink: 0, marginTop: '4px', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                    ↓
                  </div>
                </div>

                {/* Expanded body */}
                {isOpen && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '32px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '32px' }} className="hero-grid">

                      {/* Challenge */}
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-red)', marginBottom: '10px' }}>The Challenge</div>
                        <p style={{ fontSize: '14px', color: 'var(--text-m)', lineHeight: 1.75 }}>{c.challenge}</p>
                      </div>

                      {/* Solution */}
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '10px' }}>The Solution</div>
                        <p style={{ fontSize: '14px', color: 'var(--text-m)', lineHeight: 1.75 }}>{c.solution}</p>
                      </div>
                    </div>

                    {/* Results */}
                    <div style={{ background: 'var(--bg-2)', borderRadius: '6px', padding: '24px', marginBottom: '24px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-green)', marginBottom: '14px' }}>Results</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '12px' }} className="hero-grid">
                        {c.results.map((r, i) => (
                          <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                            <span style={{ color: 'var(--ms-green)', fontWeight: 700, fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>✓</span>
                            <span style={{ fontSize: '14px', color: 'var(--text-h)', lineHeight: 1.6 }}>{r}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Tech stack */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-s)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Tech:</span>
                      {c.tech.map((t, i) => (
                        <span key={i} style={{ fontSize: '12px', fontWeight: 500, padding: '3px 10px', borderRadius: '4px', background: 'var(--bg-3)', color: 'var(--text-m)', border: '1px solid var(--border)' }}>{t}</span>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <CtaStrip
        heading="Have a similar challenge?"
        subtext="Tell me what you need and I'll come back with a clear plan and fixed price within 24 hours."
        buttonLabel="Get in Touch"
        onAction={() => onNavigate('contact')}
      />
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
